/**
 * WorkbenchHostProvider — delivers the {@link WorkbenchHostContext} once.
 *
 * The composition (Studio, viewer) passes its live capabilities each render;
 * this keeps them in a mutable ref (updated every render, cheap) and builds the
 * ctx ONCE against that ref. So consumers get a referentially-stable ctx whose
 * action closures never churn, while its read getters always see the LATEST
 * capabilities — the action-identity / read-freshness split the spine needs.
 */
import { useMemo, useRef, type ReactNode } from "react";
import {
  makeHostContext,
  WorkbenchHostContextProvider,
  type HostCapabilities,
} from "./hostContext.js";

export function WorkbenchHostProvider({
  capabilities,
  children,
}: {
  readonly capabilities: HostCapabilities;
  readonly children: ReactNode;
}): ReactNode {
  const ref = useRef(capabilities);
  ref.current = capabilities;
  // Build the ctx once — stable action refs + live getters over `ref.current`.
  const ctx = useMemo(() => makeHostContext(ref), []);
  return <WorkbenchHostContextProvider value={ctx}>{children}</WorkbenchHostContextProvider>;
}
